import { sanitizeNullStrings } from "@/utils/sanitize";
import { useEffect, useMemo, useState } from "react";
import { Box, Button, Grid2, TextField, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AdjustOutlinedIcon from "@mui/icons-material/AdjustOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { ErrorModal } from "@dexteel/mesf-core";
import {
	DetailBody,
	DetailHeader,
	DetailSection,
	DetailSummary,
	DetailTabs,
} from "@/controls/detail-panel/DetailPanel";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { DeleteConfirmModal } from "@/controls/modals/DeleteConfirmModal";
import { urlParam, useUrlState } from "@/shared/hooks/useUrlState";
import type { Barrel, BarrelLayout } from "../models/Barrel";
import { usePassIds, useStands } from "../hooks/useBarrels";
import { StandSelector } from "./StandSelector";
import { RollMaterialSelector } from "./RollMaterialSelector";
import { BarrelLayoutsTable } from "./BarrelLayoutsTable";
import { AddLayoutModal } from "./AddLayoutModal";
import { BarrelAttachments } from "./BarrelAttachments";
import { useBarrelLayoutMutation } from "../hooks/useBarrelLayoutMutation";

interface FormErrors {
	Name?: string;
	StandId?: string;
}

interface BarrelDetailViewerProps {
	barrel: Barrel | null;
	existingBarrels: Barrel[];
	layouts: BarrelLayout[];
	isSaving: boolean;
	onSave: (barrel: Barrel) => void;
	onClose: () => void;
}

const TABS = ["passes", "info"] as const;
type BarrelTab = (typeof TABS)[number];

const URL_SCHEMA = {
	tab: urlParam.oneOf(TABS, "passes"),
};

const formatDiameter = (value: number | null | undefined) =>
	value === null || value === undefined ? null : value.toFixed(3);

export const BarrelDetailViewer = ({
	barrel,
	existingBarrels,
	layouts,
	isSaving,
	onSave,
	onClose,
}: BarrelDetailViewerProps) => {
	const [formData, setFormData] = useState<Barrel | null>(null);
	const [addModalOpen, setAddModalOpen] = useState(false);
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);
	const [layoutToDelete, setLayoutToDelete] = useState<BarrelLayout | null>(
		null,
	);
	const [layoutError, setLayoutError] = useState("");
	const [layoutNotification, setLayoutNotification] = useState("");
	const [errors, setErrors] = useState<FormErrors>({});
	const [touched, setTouched] = useState<Record<string, boolean>>({});

	const isNewBarrel = !barrel?.Id;
	const [{ tab }, setUrlState] = useUrlState(URL_SCHEMA);
	const activeTab: BarrelTab = isNewBarrel ? "info" : tab;

	const { data: stands = [] } = useStands();

	const millSizeName = useMemo(() => {
		if (!barrel?.StandId) return null;
		return stands.find((s) => s.Id === barrel.StandId)?.MillSizeName ?? null;
	}, [barrel?.StandId, stands]);

	const validateForm = useMemo(() => {
		return (data: Barrel | null): FormErrors => {
			const newErrors: FormErrors = {};

			if (!data?.Name || data.Name.trim().length === 0) {
				newErrors.Name = "Name is required";
			} else {
				const isDuplicate = existingBarrels.some(
					(b) =>
						b.Id !== data.Id &&
						b.Name.toLowerCase() === data.Name.trim().toLowerCase(),
				);
				if (isDuplicate) {
					newErrors.Name = "This name already exists";
				}
			}

			if (data?.StandId == null) {
				newErrors.StandId = "Stand is required";
			}

			return newErrors;
		};
	}, [existingBarrels]);

	const formErrors = useMemo(
		() => validateForm(formData),
		[formData, validateForm],
	);
	const isFormValid = Object.keys(formErrors).length === 0;
	const canCreateBarrel = isNewBarrel && isFormValid;
	const canEditLayouts = formData?.Id != null;

	const { data: passIds = [] } = usePassIds({
		standId: formData?.StandId,
		enabled: canEditLayouts && formData?.StandId != null,
	});

	const {
		addLayout,
		updateLayout,
		reorderLayout,
		deleteLayout,
		isAddingLayout,
		isUpdatingLayout,
		isReorderingLayout,
		isDeletingLayout,
	} = useBarrelLayoutMutation({
		onAddSuccess: () => {
			setLayoutNotification("Pass added successfully");
			setAddModalOpen(false);
		},
		onUpdateSuccess: () => {
			setLayoutNotification("Pass updated successfully");
		},
		onReorderSuccess: () => {
			setLayoutNotification("Pass reordered successfully");
		},
		onDeleteSuccess: () => {
			setLayoutNotification("Pass removed successfully");
			setDeleteModalOpen(false);
			setLayoutToDelete(null);
		},
		onError: (err) => setLayoutError(err.message),
	});
	const isBusy =
		isSaving ||
		isAddingLayout ||
		isUpdatingLayout ||
		isReorderingLayout ||
		isDeletingLayout;

	useEffect(() => {
		if (barrel) {
			setFormData(sanitizeNullStrings({ ...barrel }));
		} else {
			setFormData(null);
		}
		setErrors({});
		setTouched({});
	}, [barrel]);

	if (!barrel) {
		return (
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					height: "100%",
					color: "text.secondary",
				}}
			>
				<Typography>Select a barrel to view details</Typography>
			</Box>
		);
	}

	if (!formData) return null;

	const handleChange = (field: keyof Barrel, value: unknown) => {
		setFormData((prev) => (prev ? { ...prev, [field]: value } : null));
	};

	const handleBlur = (field: keyof Barrel) => {
		setTouched((prev) => ({ ...prev, [field]: true }));
		if (formData) {
			setErrors(validateForm(formData));
		}
	};

	// Text and numbers save on blur, existing barrels only.
	const handleBlurSave = (field: keyof Barrel, value: unknown) => {
		handleBlur(field);

		if (isNewBarrel) return;

		const currentErrors = validateForm(
			formData ? { ...formData, [field]: value } : null,
		);
		if (currentErrors[field as keyof FormErrors]) return;

		const originalValue = barrel[field];
		const normalizedOriginal = originalValue === null ? "" : originalValue;
		const normalizedNew = value === null ? "" : value;

		if (normalizedOriginal !== normalizedNew) {
			onSave({ ...formData, [field]: value });
		}
	};

	// Selects save as soon as they change.
	const handleSelectChange = (field: keyof Barrel, value: unknown) => {
		handleChange(field, value);
		setTouched((prev) => ({ ...prev, [field]: true }));

		const updatedData = formData ? { ...formData, [field]: value } : null;
		const currentErrors = validateForm(updatedData);
		setErrors(currentErrors);

		if (isNewBarrel) return;
		if (currentErrors[field as keyof FormErrors]) return;

		if (barrel[field] !== value) {
			onSave({ ...formData, [field]: value });
		}
	};

	const handleCreate = () => {
		setTouched({ Name: true, StandId: true });
		setErrors(formErrors);

		if (formData && canCreateBarrel) {
			onSave(formData);
		}
	};

	const handleSaveLayout = (passId: number) => {
		if (formData?.Id) {
			addLayout({ barrelId: formData.Id, passId });
		}
	};

	const handleUpdateLayout = (layoutId: number, newPassId: number) => {
		if (formData?.Id) {
			updateLayout({ id: layoutId, barrelId: formData.Id, passId: newPassId });
		}
	};

	const handleReorderLayout = (layoutId: number, newPosition: number) => {
		reorderLayout({ id: layoutId, newPosition });
	};

	const handleDeleteLayout = (layout: BarrelLayout) => {
		setLayoutToDelete(layout);
		setDeleteModalOpen(true);
	};

	const numberField = (
		field:
			| "TopDiameter"
			| "TopDiameterScrap"
			| "BottomDiameter"
			| "BottomDiameterScrap",
		label: string,
	) => (
		<TextField
			label={label}
			type="number"
			value={formData[field]}
			onChange={(e) => handleChange(field, Number(e.target.value))}
			onBlur={(e) => handleBlurSave(field, Number(e.target.value))}
			fullWidth
			size="small"
			slotProps={{
				inputLabel: { shrink: true },
				htmlInput: { step: 0.001 },
			}}
		/>
	);

	// Top and bottom share the material on all but one barrel, so the pair only shows apart.
	const material =
		barrel.TopRollMaterialName === barrel.BottomRollMaterialName
			? barrel.TopRollMaterialName
			: `${barrel.TopRollMaterialName ?? "-"} / ${barrel.BottomRollMaterialName ?? "-"}`;

	return (
		<Box
			sx={{
				height: "100%",
				display: "flex",
				flexDirection: "column",
				overflow: "hidden",
			}}
		>
			<DetailHeader
				title={isNewBarrel ? "New Barrel" : formData.Name}
				active={isNewBarrel ? undefined : barrel.Active}
				isSaving={isBusy}
				onClose={onClose}
				actions={
					isNewBarrel && (
						<Button
							color="primary"
							variant="contained"
							size="small"
							onClick={handleCreate}
							disabled={!canCreateBarrel || isSaving}
						>
							{isSaving ? "CREATING..." : "CREATE"}
						</Button>
					)
				}
			/>

			<DetailBody>
				{!isNewBarrel && (
					<DetailSummary
						items={[
							{ label: "Stand", value: barrel.StandName, grow: 0.6 },
							{ label: "Mill Size", value: millSizeName, grow: 0.6 },
							{
								label: "Top Diam",
								value: formatDiameter(barrel.TopDiameter),
								grow: 0.7,
							},
							{
								label: "Bottom Diam",
								value: formatDiameter(barrel.BottomDiameter),
								grow: 0.9,
							},
							{ label: "Material", value: material, grow: 1.3 },
						]}
					/>
				)}

				<DetailTabs<BarrelTab>
					value={activeTab}
					onChange={(next) => setUrlState({ tab: next })}
					flush={activeTab === "passes"}
					actions={
						activeTab === "passes" && (
							<Button
								variant="contained"
								color="primary"
								size="small"
								startIcon={<AddIcon />}
								onClick={() => setAddModalOpen(true)}
								disabled={isBusy || formData.StandId == null}
							>
								ADD PASS
							</Button>
						)
					}
					tabs={[
						{ value: "info", label: "Barrel Info" },
						{
							value: "passes",
							label: "Assigned Passes",
							disabled: isNewBarrel,
						},
					]}
				>
					{activeTab === "passes" && canEditLayouts ? (
						<BarrelLayoutsTable
							layouts={layouts}
							passIds={passIds}
							isEditing={canEditLayouts}
							onUpdate={handleUpdateLayout}
							onReorder={handleReorderLayout}
							onDelete={handleDeleteLayout}
						/>
					) : (
						<>
							<DetailSection
								title="Basic Information"
								icon={<InfoOutlinedIcon />}
							>
								<Grid2 container spacing={1}>
									<Grid2 size={{ xs: 12, md: 6 }}>
										<TextField
											label="Name"
											value={formData.Name}
											onChange={(e) => handleChange("Name", e.target.value)}
											onBlur={(e) => handleBlurSave("Name", e.target.value)}
											fullWidth
											size="small"
											required
											error={touched.Name && !!errors.Name}
											helperText={touched.Name && errors.Name}
											slotProps={{ inputLabel: { shrink: true } }}
										/>
									</Grid2>
									<Grid2 size={{ xs: 12, md: 3 }}>
										<StandSelector
											value={formData.StandId}
											onChange={(value) => handleSelectChange("StandId", value)}
											fullWidth
											required
											size="small"
											error={touched.StandId && !!errors.StandId}
											helperText={touched.StandId ? errors.StandId : undefined}
										/>
									</Grid2>
									<Grid2 size={{ xs: 12, md: 3 }}>
										<TextField
											label="Pit Program Name"
											value={formData.PitProgramName ?? ""}
											onChange={(e) =>
												handleChange("PitProgramName", e.target.value || null)
											}
											onBlur={(e) =>
												handleBlurSave("PitProgramName", e.target.value || null)
											}
											fullWidth
											size="small"
											slotProps={{ inputLabel: { shrink: true } }}
										/>
									</Grid2>
									<Grid2 size={{ xs: 12 }}>
										<TextField
											label="Comments"
											value={formData.Comments ?? ""}
											onChange={(e) => handleChange("Comments", e.target.value)}
											onBlur={(e) => handleBlurSave("Comments", e.target.value)}
											fullWidth
											size="small"
											slotProps={{ inputLabel: { shrink: true } }}
										/>
									</Grid2>
								</Grid2>
							</DetailSection>

							<DetailSection title="Rolls" icon={<AdjustOutlinedIcon />}>
								<Grid2 container spacing={1}>
									<Grid2 size={{ xs: 12, md: 4 }}>
										{numberField("TopDiameter", "Top Diameter")}
									</Grid2>
									<Grid2 size={{ xs: 12, md: 4 }}>
										{numberField("TopDiameterScrap", "Top Diameter Scrap")}
									</Grid2>
									<Grid2 size={{ xs: 12, md: 4 }}>
										<RollMaterialSelector
											value={formData.TopRollMaterialId}
											onChange={(value) =>
												handleSelectChange("TopRollMaterialId", value)
											}
											label="Top Roll Material"
											fullWidth
											size="small"
										/>
									</Grid2>
									<Grid2 size={{ xs: 12, md: 4 }}>
										{numberField("BottomDiameter", "Bottom Diameter")}
									</Grid2>
									<Grid2 size={{ xs: 12, md: 4 }}>
										{numberField(
											"BottomDiameterScrap",
											"Bottom Diameter Scrap",
										)}
									</Grid2>
									<Grid2 size={{ xs: 12, md: 4 }}>
										<RollMaterialSelector
											value={formData.BottomRollMaterialId}
											onChange={(value) =>
												handleSelectChange("BottomRollMaterialId", value)
											}
											label="Bottom Roll Material"
											fullWidth
											size="small"
										/>
									</Grid2>
								</Grid2>
							</DetailSection>

							<DetailSection title="Drawing" icon={<DescriptionOutlinedIcon />}>
								<Grid2 container spacing={1}>
									<Grid2 size={{ xs: 12, md: 6 }}>
										<TextField
											label="DWG File Name"
											value={formData.DwgFileName ?? ""}
											onChange={(e) =>
												handleChange("DwgFileName", e.target.value)
											}
											onBlur={(e) =>
												handleBlurSave("DwgFileName", e.target.value)
											}
											fullWidth
											size="small"
											slotProps={{ inputLabel: { shrink: true } }}
										/>
									</Grid2>
									<Grid2 size={{ xs: 12 }}>
										<BarrelAttachments
											barrelId={formData.Id}
											disabled={isSaving}
										/>
									</Grid2>
								</Grid2>
							</DetailSection>
						</>
					)}
				</DetailTabs>
			</DetailBody>

			<AddLayoutModal
				open={addModalOpen}
				standId={formData.StandId}
				onClose={() => setAddModalOpen(false)}
				onSave={handleSaveLayout}
				isSaving={isAddingLayout}
			/>

			<DeleteConfirmModal
				open={deleteModalOpen}
				title="REMOVE PASS"
				itemName={layoutToDelete?.PassName ?? ""}
				isDeleting={isDeletingLayout}
				onConfirm={() => layoutToDelete && deleteLayout(layoutToDelete.Id)}
				onCancel={() => {
					setDeleteModalOpen(false);
					setLayoutToDelete(null);
				}}
			/>

			<NotificationSnackBar
				message={layoutNotification}
				onHide={() => setLayoutNotification("")}
			/>
			<ErrorModal error={layoutError} onHide={() => setLayoutError("")} />
		</Box>
	);
};
